import type {
    CaseInputs,
    CashPolicy,
    EvaluateContext,
    History,
    Model,
    SchemaEntry,
} from './types';

export interface SolveMaxDistributionParams {
    evaluate: (
        schema: SchemaEntry[],
        history: History,
        inputs: CaseInputs,
        lastClosedMonth: string,
        context?: EvaluateContext,
    ) => Model;
    schema: SchemaEntry[];
    history: History;
    inputs: CaseInputs;
    context: EvaluateContext;
    horizonMonths: number;
    step: number;
    upper: number;
    lastClosedMonth: string;
}

export interface SolveMaxDistributionResult {
    amount: number;
    rounded_down_to_step: number;
    binding_month_t: number | null;
    min_operating_cash_at_amount: number;
}

function clonePolicy(policy: CashPolicy): CashPolicy {
    return structuredClone(policy);
}

function isFeasible(
    model: Model,
    horizonMonths: number,
): boolean {
    if (!model.cash_policy) {
        return false;
    }
    const effectiveFloor = model.cash_policy.effective_floor;
    for (const month of model.cash_policy.months) {
        if (month.t <= horizonMonths) {
            if (month.breach) {
                return false;
            }
            if (month.operating_cash < effectiveFloor - 0.005) {
                return false;
            }
        }
    }
    return true;
}

function evaluateAtAmount(
    params: SolveMaxDistributionParams,
    amount: number,
): Model {
    const inputs = structuredClone(params.inputs);
    if (!inputs.cash_policy?.enabled) {
        return params.evaluate(
            params.schema,
            params.history,
            inputs,
            params.lastClosedMonth,
            params.context,
        );
    }
    inputs.cash_policy = clonePolicy(inputs.cash_policy);
    inputs.cash_policy.distribution.amount_per_period = amount;
    inputs.cash_policy.distribution.split_schedule = null;
    inputs.cash_policy.sweep = {
        ...inputs.cash_policy.sweep,
        enabled: false,
    };
    return params.evaluate(
        params.schema,
        params.history,
        inputs,
        params.lastClosedMonth,
        params.context,
    );
}

function minOperatingCash(model: Model, horizonMonths: number): number {
    if (!model.cash_policy) {
        return 0;
    }
    let min = Infinity;
    for (const month of model.cash_policy.months) {
        if (month.t <= horizonMonths) {
            min = Math.min(min, month.operating_cash);
        }
    }
    return min === Infinity ? 0 : min;
}

export function bindingMonthAtAmount(
    params: SolveMaxDistributionParams,
    amount: number,
    horizonMonths: number,
): number | null {
    const testAmount = Math.min(amount + params.step, params.upper);
    const model = evaluateAtAmount(params, testAmount);
    if (!model.cash_policy) {
        return null;
    }
    for (const month of model.cash_policy.months) {
        if (month.t <= horizonMonths && month.breach) {
            return month.t;
        }
    }
    return null;
}

export function solveMaxDistribution(
    params: SolveMaxDistributionParams,
): SolveMaxDistributionResult {
    const { step, upper, horizonMonths } = params;

    const upperModel = evaluateAtAmount(params, upper);
    if (isFeasible(upperModel, horizonMonths)) {
        return {
            amount: upper,
            rounded_down_to_step: step,
            binding_month_t: null,
            min_operating_cash_at_amount: minOperatingCash(upperModel, horizonMonths),
        };
    }

    const zeroModel = evaluateAtAmount(params, 0);
    const zeroMin = minOperatingCash(zeroModel, horizonMonths);
    if (!isFeasible(zeroModel, horizonMonths)) {
        return {
            amount: 0,
            rounded_down_to_step: step,
            binding_month_t: bindingMonthAtAmount(params, 0, horizonMonths),
            min_operating_cash_at_amount: zeroMin,
        };
    }

    let lo = 0;
    let hi = upper;
    let best = 0;

    while (hi - lo > step) {
        const mid = Math.floor((lo + hi) / 2 / step) * step;
        const testAmount = mid === lo ? lo + step : mid;
        const model = evaluateAtAmount(params, testAmount);
        if (isFeasible(model, horizonMonths)) {
            best = testAmount;
            lo = testAmount;
        } else {
            hi = testAmount;
        }
    }

    const amount = Math.floor(best / step) * step;
    const finalModel = evaluateAtAmount(params, amount);
    const binding = bindingMonthAtAmount(params, amount, horizonMonths);

    return {
        amount,
        rounded_down_to_step: step,
        binding_month_t: binding,
        min_operating_cash_at_amount: minOperatingCash(finalModel, horizonMonths),
    };
}
